import { useId, useRef } from 'react'
import AttachmentChips from '@features/shared/components/AttachmentChips'
import { RequirementMarkLabel } from '@features/shared/components/FormRequirements'
import { useRequirementMark } from '@features/shared/components/requirementMarks'
import { PaperclipIcon } from '@features/shared/icons/icons'
import { SUPPORT_ATTACHMENT_ACCEPT, type SupportAttachments } from '../hooks/useSupportAttachments'

/**
 * The support form's screenshots and PDFs: picked from the button, or dropped
 * or pasted anywhere on the form (the panel handles those, `dragging` while a
 * file is over it). Each shows as a chip with its upload's progress or problem.
 */
export default function SupportAttachmentsField({
  files,
  dragging,
  disabled = false,
}: {
  files: SupportAttachments
  dragging: boolean
  disabled?: boolean
}) {
  const requirementMark = useRequirementMark({ required: false })
  const inputRef = useRef<HTMLInputElement>(null)
  const labelId = useId()
  const max = files.limits?.max_files_per_request ?? 0
  const full = files.items.length >= max

  return (
    <div className="field-input block" role="group" aria-labelledby={labelId}>
      <div className="flex items-center justify-between">
        <span id={labelId} className="field-input-label text-xs font-[450] leading-4 text-muted">
          Attachments
        </span>
        <RequirementMarkLabel mark={requirementMark} />
      </div>
      <AttachmentChips
        items={files.items}
        notice={files.notice}
        onRemove={files.remove}
        className="mt-1"
      />
      {full ? null : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className={`mt-1 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            dragging
              ? 'border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] text-[color:var(--text-info)]'
              : 'border-default text-muted enabled:hover:bg-muted enabled:hover:text-default'
          }`}
        >
          <PaperclipIcon size={14} aria-hidden="true" />
          {dragging ? 'Drop files to attach' : 'Attach screenshots or PDFs'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={SUPPORT_ATTACHMENT_ACCEPT}
        className="hidden"
        data-testid="support-attachment-input"
        onChange={(event) => {
          files.addFiles(Array.from(event.target.files ?? []))
          // The same file can be picked again after it's removed.
          event.target.value = ''
        }}
      />
      {full ? null : (
        <p className="mt-1 text-xs text-muted">
          Up to {max} files: images up to{' '}
          {Math.round((files.limits?.image_max_bytes ?? 0) / 2 ** 20)} MB, PDFs up to{' '}
          {Math.round((files.limits?.pdf_max_bytes ?? 0) / 2 ** 20)} MB. You can also drop or paste
          them here.
        </p>
      )}
    </div>
  )
}
